import { PropsWithChildren } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader } from '../../components/Loader';
import { usePersonasEnabled } from '../../hooks/usePersonasEnabled';
import { ROUTES } from '../consts';

type PersonasProtectedRouteProps = PropsWithChildren<{
  fallbackTo?: string;
}>;

/**
 * Gate for the Team Profiles (Developer Personas) routes. Renders its
 * children only while the personas feature is available for the workspace;
 * otherwise redirects to the fallback so a disabled feature is inert — a
 * direct navigation (or stale bookmark) lands on the portal home instead of
 * mounting the page, firing `usePersonasQuery`, and rendering an error state
 * off the resulting 503.
 *
 * Mirrors `FeatureFlagProtectedRoute` (loader while resolving → `<Navigate>`
 * when unavailable). The only difference is the signal: personas availability
 * comes from 503 capability detection (`usePersonasEnabled`) rather than the
 * platform feature-flag service, because the personas master gate lives in
 * server-side settings, not the feature registry.
 */
export const PersonasProtectedRoute = ({
  children,
  fallbackTo = ROUTES.ROOT,
}: PersonasProtectedRouteProps) => {
  const { isPersonasEnabled, isPersonasLoading } = usePersonasEnabled();

  if (isPersonasLoading) {
    return <Loader center />;
  }

  if (!isPersonasEnabled) {
    return <Navigate to={fallbackTo} replace />;
  }

  return <>{children}</>;
};
